/**
 * IconTile — Sprint-16-Brand.
 *
 * Wiederkehrendes Kacheln-Atom für Card-Header-Icons, Akte-Kategorie-Cards (4g),
 * KPI-Hero-Icons und Sidebar-Nav.
 *
 * Palette (Soft-Tint + Full-Tone-Icon):
 *   - accent/sage:  Neutral-Positive (Live-Status, Success-KPI)
 *   - navy:         Struktur/Info (Verträge, System-Kategorien)
 *   - postmark:     Alternative Info-Cast (deeper contrast)
 *   - sealwax:      Achtung / Auto-Inbox / Rechnungen (4g #03)
 *   - amber:        Warnung / Photos (4g #08)
 *   - warning:      Alias von amber
 *   - error:        Alias von sealwax
 *   - success:      Alias von sage
 *   - muted:        Neutral-inaktiv (leere Cards)
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const iconTileVariants = cva(
  ['grid place-items-center', 'rounded-xl', 'transition-colors duration-fast'],
  {
    variants: {
      variant: {
        accent: 'bg-sage/15 text-sage',
        navy: 'bg-navy/15 text-navy',
        postmark: 'bg-postmark/15 text-postmark',
        sealwax: 'bg-sealwax/15 text-sealwax',
        amber: 'bg-amber/15 text-amber',
        muted: 'bg-card-alt text-ink-soft border border-line/50',
        // Legacy-Aliase
        success: 'bg-sage/15 text-sage',
        warning: 'bg-amber/15 text-amber',
        error: 'bg-sealwax/15 text-sealwax',
      },
      size: {
        sm: 'size-6', // 24 — Sidebar-Nav
        md: 'size-11', // 44 — Standard
        lg: 'size-14', // 56 — KPI-Hero, Success-State
      },
    },
    defaultVariants: {
      variant: 'accent',
      size: 'md',
    },
  },
);

export interface IconTileProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof iconTileVariants> {}

export const IconTile = forwardRef<HTMLDivElement, IconTileProps>(
  ({ className, variant, size, children, ...props }, ref) => (
    <div ref={ref} className={cn(iconTileVariants({ variant, size }), className)} {...props}>
      {children}
    </div>
  ),
);
IconTile.displayName = 'IconTile';

export { iconTileVariants };
